/**
 * Colour-aware sold-out marks on the PDP variant picker (dropdown picker, pdp-footwear).
 *
 * Every colour of a style is its own product, so "sold out" depends on which colour you mean.
 * snippets/product-variant-picker-dropdown.liquid renders the in-stock variants of every colour
 * as JSON (script[data-color-availability]) and this file applies the rules:
 *
 *  - Size / Width boxes follow the colour on screen: a box is crossed out when this colour has
 *    nothing in stock for it, within the options picked before it (Size is checked within the
 *    picked Width). When another colour does have it, the box reads "Sold out in this color"
 *    (screen readers); when none does it reads "Sold out". The mobile size list rows are
 *    struck through the same way and show that text. The page renders the same rule
 *    server-side, so nothing flashes on load. Crossed-out boxes stay clickable (Notify Me).
 *  - Every colour shows the same sizes in the same places: a size only other colours make has
 *    a crossed-out stand-in box (rendered by the snippet, no radio). Tapping it remembers that
 *    size (see below) and drops this colour's pick for that option.
 *  - Colour swatches follow the picked size: a colour is crossed out when it has nothing in
 *    stock for what the shopper picked (Size, plus Width when picked). Before any pick, or when
 *    the picked size is sold out in every colour, only a colour sold out in every size is
 *    crossed out. Crossed-out colours stay clickable.
 *  - A line under the Add to Cart button explains a pick this colour can't fill: "11 US is sold
 *    out in Ivory but in stock in 9 other colors." with a "Show colors" shortcut, "11.5 US
 *    isn't available in Ivory ..." for a remembered size it doesn't make, and "Ivory is sold out
 *    in every size." for a colour with nothing in stock. It sits under the button so it never
 *    pushes the sizes or the button when it comes and goes.
 *  - Switching colour keeps the Size and Width the shopper picked. assets/instant-colorway.js
 *    carries them (it asks wanted() for them); the theme's own switch is handled here. A size
 *    the new colour doesn't make is remembered (a "ghost" pick): it crosses colours out and
 *    explains itself in the line under the button like a pick, and is picked again on the next
 *    colour that has it. Picking another value of that option forgets it.
 *
 * "Picked" means picked by the shopper, as tracked by assets/add-to-cart-button.js. The server
 * pre-fills its own guess into every re-rendered picker, and a guess must not cross colours out.
 */
(function () {
  "use strict";

  if (window.AetrexColorAvailability) return;

  var DATA_SELECTOR = "script[data-color-availability]";
  var SWATCH_SELECTOR = ".custom-meta-url[data-product-handle]";
  var OPTION_INPUT = "input.custom-option-buttons";
  var PLACEHOLDER = "[data-ca-placeholder]";
  var GATED_SLUGS = ["size", "width"]; // the options add-to-cart-button.js tracks as picks
  var UNAVAILABLE_CLASS = "is-color-unavailable";
  var CARRY_MAX_AGE = 10000; // ms between a colour click and the switch it started
  var SETTLE_MIN = 150; // the theme clears every pick 100 ms after a switch
  var SETTLE_MAX = 3000;
  var SHOW_AVAILABILITY_NOTE = false; // the whole line under Add to Cart — set true to re-enable

  var hookedInfos = new WeakSet();
  var clickToken = 0;
  var lastClick = null; // { token, at, picks: [{ name, value }] }: picks at the moment of a colour click
  var pending = null; // picks being carried to the colour just switched to, until they are re-applied
  var ghosts = new Map(); // norm(option name) -> { name, value }: remembered picks this colour doesn't make

  function norm(value) {
    return String(value == null ? "" : value)
      .trim()
      .toLowerCase();
  }

  function cssEscape(value) {
    return window.CSS && CSS.escape
      ? CSS.escape(value)
      : String(value).replace(/["\\]/g, "\\$&");
  }

  // assets/instant-colorway.js is running (it gives up on a page it can't handle).
  function instantSwitch() {
    return !!(window.__instantColorway && window.__instantColorway.active);
  }

  /* ---------- data ---------- */

  function parseData(root) {
    var el =
      root && root.querySelector ? root.querySelector(DATA_SELECTOR) : null;
    if (!el) return null;

    var raw;
    try {
      raw = JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
    if (!raw || !raw.products) return null;

    var products = new Map();
    Object.keys(raw.products).forEach(function (handle) {
      var entry = raw.products[handle] || {};
      products.set(handle, {
        names: (entry.o || []).map(norm),
        rows: (entry.v || []).map(function (row) {
          return (row || []).map(norm);
        }),
      });
    });

    return { current: raw.current, products: products, t: raw.t || {} };
  }

  // True when the colour has an in-stock variant matching every [option, value] pair.
  // A colour that doesn't have one of the options is not held to it.
  function inStock(entry, pairs) {
    if (!entry) return false;
    var indexes = pairs.map(function (pair) {
      return entry.names.indexOf(pair[0]);
    });
    return entry.rows.some(function (row) {
      return pairs.every(function (pair, i) {
        return indexes[i] === -1 || row[indexes[i]] === pair[1];
      });
    });
  }

  function inStockAnywhere(data, pairs) {
    var found = false;
    data.products.forEach(function (entry) {
      if (!found && inStock(entry, pairs)) found = true;
    });
    return found;
  }

  function pairsOf(picks) {
    return picks.map(function (pick) {
      return [pick.name, pick.value];
    });
  }

  /* ---------- picker helpers ---------- */

  function optionFieldsets(picker) {
    return Array.prototype.filter.call(
      picker.querySelectorAll("fieldset[data-option-slug]"),
      function (fieldset) {
        return !!fieldset.querySelector(OPTION_INPUT);
      },
    );
  }

  function radiosOf(fieldset) {
    return Array.prototype.slice.call(fieldset.querySelectorAll(OPTION_INPUT));
  }

  function rawOptionName(fieldset) {
    var radio = fieldset.querySelector(OPTION_INPUT);
    return radio.getAttribute("data-opt-name") || radio.name;
  }

  function optionName(fieldset) {
    return norm(rawOptionName(fieldset));
  }

  // Width narrows Size whatever order a colour lists its options in (it varies within a style).
  function narrowRank(fieldset, position) {
    var slug = fieldset.getAttribute("data-option-slug");
    if (slug === "width") return -2;
    if (slug === "size") return -1;
    return position;
  }

  function hasStandIns(fieldset) {
    return !!fieldset.querySelector(".label-swatch" + PLACEHOLDER);
  }

  // How a value reads in a sentence: "wide" -> "Wide", sizes as they are.
  function display(value) {
    var text = String(value == null ? "" : value).trim();
    return /\d/.test(text) ? text : titleCase(text);
  }

  // Visible pickers that carry the colour data. The theme keeps the old picker hidden next to
  // the new one for a moment after each pick, so hidden ones are skipped.
  function livePickers(info) {
    return Array.prototype.filter.call(
      info.querySelectorAll("variant-picker"),
      function (picker) {
        return (
          picker.style.display !== "none" &&
          !!picker.querySelector(DATA_SELECTOR)
        );
      },
    );
  }

  function livePicker() {
    var found = null;
    Array.prototype.some.call(
      document.querySelectorAll("product-info"),
      function (info) {
        var picker = livePickers(info)[0];
        if (picker) found = { info: info, picker: picker };
        return !!found;
      },
    );
    return found;
  }

  function selectionState() {
    var manager = window.addToCartButtonManager;
    if (
      !manager ||
      !manager.mainButton ||
      !manager.selection ||
      !(manager.selection.chosen instanceof Map)
    )
      return null;
    return manager.selection;
  }

  // What the shopper picked, in option order:
  // [{ fieldset, slug, name, rawName, value, label, ghost }]. A remembered pick this colour
  // doesn't make (ghost) stands in for an option left open here.
  function picksFor(picker, info) {
    var selection = selectionState();
    var productId = info ? info.getAttribute("data-product-id") : null;
    var trusted =
      !!selection &&
      (selection.productId == null ||
        String(selection.productId) === String(productId));
    var out = [];

    optionFieldsets(picker).forEach(function (fieldset) {
      var radios = radiosOf(fieldset);
      if (radios.length < 2) return; // a single value is not a choice

      var slug = fieldset.getAttribute("data-option-slug");
      var name = optionName(fieldset);
      var value = null;

      if (selection && GATED_SLUGS.indexOf(slug) !== -1) {
        if (trusted && selection.chosen.has(slug))
          value = selection.chosen.get(slug);
      } else {
        var checked = radios.filter(function (radio) {
          return radio.checked;
        })[0];
        if (checked) value = checked.value;
      }

      // Carried over from the previous colour and about to be re-applied.
      if (value == null && pending && pending[name] != null) {
        var match = radios.filter(function (radio) {
          return norm(radio.value) === norm(pending[name]);
        })[0];
        if (match) value = match.value;
      }

      if (value != null && value !== "") {
        out.push({
          fieldset: fieldset,
          slug: slug,
          name: name,
          rawName: rawOptionName(fieldset),
          value: norm(value),
          label: String(value),
          ghost: false,
        });
        return;
      }

      var ghost = ghosts.get(name);
      var offered =
        ghost &&
        radios.some(function (radio) {
          return norm(radio.value) === norm(ghost.value);
        });
      if (ghost && !offered) {
        out.push({
          fieldset: fieldset,
          slug: slug,
          name: name,
          rawName: ghost.name,
          value: norm(ghost.value),
          label: String(ghost.value),
          ghost: true,
        });
      }
    });

    return out;
  }

  // Options this colour makes in one value only, next to stand-ins for the others, while the
  // shopper's remembered value is one of those others: [{ label, only }]. That one value is
  // picked by the page, so this is only explained, not shown as a pick.
  function onlyOnes(picker) {
    var out = [];
    optionFieldsets(picker).forEach(function (fieldset) {
      var radios = radiosOf(fieldset);
      if (radios.length !== 1 || !hasStandIns(fieldset)) return;
      var ghost = ghosts.get(optionName(fieldset));
      if (!ghost || norm(ghost.value) === norm(radios[0].value)) return;
      out.push({ label: ghost.value, only: radios[0].value });
    });
    return out;
  }

  /* ---------- size / width boxes and the mobile size list ---------- */

  function soldOutText(data) {
    return data.t.soldOut || "Sold out";
  }

  function soldOutIcon(picker, doc) {
    var span = doc.createElement("span");
    span.className =
      "unavailable-icon absolute inset-0 flex items-center justify-center pointer-events-none";
    span.setAttribute("aria-hidden", "true");
    var template =
      picker.querySelector("template[data-sold-out-icon]") ||
      document.querySelector("template[data-sold-out-icon]");
    if (template) span.innerHTML = template.innerHTML;
    return span;
  }

  // status: '' = open, 'all' = sold out in every colour, 'color' = sold out in the colour on
  // screen while another colour has it.
  function markBox(picker, fieldset, radio, status, data) {
    var crossed = !!status;
    var reason =
      status === "color"
        ? data.t.soldOutInColor || soldOutText(data)
        : soldOutText(data);
    var label = radio.id
      ? fieldset.querySelector('label[for="' + cssEscape(radio.id) + '"]')
      : null;
    var icon = label ? label.querySelector(":scope > .unavailable-icon") : null;
    var srText = label
      ? label.querySelector(":scope > [data-sold-out-text]")
      : null;
    var srWanted = " - " + reason;
    if (
      radio.classList.contains("disabled") === crossed &&
      (!label ||
        (!!icon === crossed &&
          (!crossed || (!!srText && srText.textContent === srWanted))))
    ) {
      return;
    }

    var value = radio.value;

    radio.classList.toggle("disabled", crossed);
    if (crossed) {
      radio.removeAttribute("aria-disabled");
      radio.setAttribute("aria-label", value + srWanted);
    } else {
      radio.removeAttribute("aria-disabled");
      radio.removeAttribute("aria-label");
    }

    if (!label) return;

    if (crossed) {
      label.removeAttribute("aria-disabled");
      label.setAttribute("data-ca-unavailable", "");
      label.setAttribute("aria-label", value + srWanted);

      var doc = label.ownerDocument || document;
      var anchor = label.querySelector(
        ":scope > .custom-alternative-size, :scope > .label-swatch__stock",
      );
      if (!srText) {
        srText = doc.createElement("span");
        srText.className = "sr-only";
        srText.setAttribute("data-sold-out-text", "");
        label.insertBefore(srText, anchor);
      }
      if (srText.textContent !== srWanted) srText.textContent = srWanted;
      if (!icon) label.insertBefore(soldOutIcon(picker, doc), anchor);
    } else {
      label.removeAttribute("aria-disabled");
      label.removeAttribute("data-ca-unavailable");
      label.setAttribute("aria-label", value);
      if (srText) srText.remove();
      if (icon) icon.remove();
    }
  }

  function markRow(fieldset, value, anyColour, thisColour, data) {
    var row = Array.prototype.filter.call(
      fieldset.querySelectorAll(
        'size-dropdown [role="option"][data-value]:not(' + PLACEHOLDER + ")",
      ),
      function (el) {
        return el.getAttribute("data-value") === value;
      },
    )[0];
    if (!row) return;

    var text = "";
    if (!anyColour) text = soldOutText(data);
    else if (!thisColour) text = data.t.soldOutInColor || soldOutText(data);
    else text = row.getAttribute("data-low-stock") || "";

    row.classList.remove("is-unavailable");
    row.classList.toggle("is-ca-unavailable", !thisColour);
    row.classList.toggle("is-other-colors", anyColour && !thisColour);

    var stock = row.querySelector(".size-dropdown__option-stock");
    if (text) {
      if (!stock) {
        stock = (row.ownerDocument || document).createElement("span");
        stock.className = "size-dropdown__option-stock";
        row.appendChild(stock);
      }
      if (stock.textContent !== text) stock.textContent = text;
    } else if (stock) {
      stock.remove();
    }
  }

  // The stand-in box of the remembered size gets the picked ring.
  function markPlaceholders(picker, picks) {
    Array.prototype.forEach.call(
      picker.querySelectorAll(".label-swatch" + PLACEHOLDER),
      function (box) {
        var name = norm(box.getAttribute("data-ca-option"));
        var on = picks.some(function (pick) {
          return (
            pick.ghost &&
            pick.name === name &&
            pick.value === norm(box.getAttribute("data-ca-placeholder"))
          );
        });
        if (box.classList.contains("checked") !== on)
          box.classList.toggle("checked", on);
        if (box.getAttribute("aria-pressed") !== String(on))
          box.setAttribute("aria-pressed", String(on));
      },
    );
  }

  // The colour on screen has nothing in stock at all.
  function isDead(data) {
    var current = data.products.get(data.current);
    return !!current && current.rows.length === 0;
  }

  function updateOptions(picker, data, picks) {
    var current = data.products.get(data.current);
    var fieldsets = optionFieldsets(picker);
    // A remembered size this colour doesn't make narrows nothing here: the shopper can't buy it.
    var real = picks.filter(function (pick) {
      return !pick.ghost;
    });

    fieldsets.forEach(function (fieldset, position) {
      var name = optionName(fieldset);
      var rank = narrowRank(fieldset, position);
      // Only options ranked before this one narrow it (Width narrows Size, not the reverse).
      var before = pairsOf(
        real.filter(function (pick) {
          var at = fieldsets.indexOf(pick.fieldset);
          return at !== -1 && narrowRank(pick.fieldset, at) < rank;
        }),
      );

      radiosOf(fieldset).forEach(function (radio) {
        var pairs = before.concat([[name, norm(radio.value)]]);
        var anyColour = inStockAnywhere(data, pairs);
        var thisColour = current ? inStock(current, pairs) : anyColour;
        var status = thisColour ? "" : anyColour ? "color" : "all";
        markBox(picker, fieldset, radio, status, data);
        markRow(fieldset, radio.value, anyColour, thisColour, data);
      });
    });

    markPlaceholders(picker, picks);
  }

  /* ---------- colour swatches ---------- */

  function markColour(swatch, crossed, data) {
    if (swatch.classList.contains(UNAVAILABLE_CLASS) !== crossed)
      swatch.classList.toggle(UNAVAILABLE_CLASS, crossed);

    var soldOut = soldOutText(data);

    // A swatch link named by aria-label (desktop and mobile).
    var name = swatch.getAttribute("data-color-label");
    if (name != null) {
      var label = crossed ? name + " - " + soldOut : name;
      if (swatch.getAttribute("aria-label") !== label)
        swatch.setAttribute("aria-label", label);
    }

    // Older markup named by its text, with the status in a visually hidden span.
    var status = swatch.querySelector("[data-color-status]");
    if (status) {
      var text = crossed ? " - " + soldOut : "";
      if (status.textContent !== text) status.textContent = text;
    }
  }

  function updateColours(roots, data, picks) {
    var pairs = pairsOf(picks);

    // A pick that no colour has in stock (a size sold out everywhere) says nothing about the
    // colours: crossing them all out would make the whole style look sold out. Drop such picks,
    // last first (Size before Width), until something is in stock somewhere.
    while (pairs.length && !inStockAnywhere(data, pairs)) pairs.pop();

    roots.forEach(function (root) {
      if (!root) return;
      Array.prototype.forEach.call(
        root.querySelectorAll(SWATCH_SELECTOR),
        function (swatch) {
          var entry = data.products.get(
            swatch.getAttribute("data-product-handle"),
          );
          if (!entry) return; // a colour the data doesn't know about is left as rendered
          markColour(swatch, !inStock(entry, pairs), data);
        },
      );
    });
  }

  /* ---------- the line under the button ---------- */

  var NOTE_ATTR = "data-color-availability-note";
  var SHOW_ATTR = "data-color-availability-show";

  function fill(template, values) {
    return String(template == null ? "" : template).replace(
      /\[(\w+)\]/g,
      function (match, key) {
        return values[key] != null ? values[key] : match;
      },
    );
  }

  function titleCase(text) {
    return text.replace(/(^|\s)(\S)/g, function (match, space, letter) {
      return space + letter.toUpperCase();
    });
  }

  // The colour on screen, as the colour label shows it ("Antique Rose").
  function colourName(picker, data) {
    var active = picker.querySelector(
      SWATCH_SELECTOR + '[aria-current="true"]',
    );
    var text = active ? active.getAttribute("data-color-label") || "" : "";
    if (!text) {
      var label = picker.querySelector(".form__label--color .font-medium");
      text = label ? label.textContent : "";
    }
    text = titleCase(text.trim());
    return text || data.t.thisColor;
  }

  // Right under the main Add to Cart form, so it pushes neither the sizes nor the button. On a
  // page without that form it goes at the end of the picker, as before.
  function noteSpot(info, picker) {
    var form = info
      ? info.querySelector('form[data-type="add-to-cart-form"]')
      : null;
    if (form && form.parentNode)
      return { parent: form.parentNode, before: form.nextSibling };
    return { parent: picker, before: null };
  }

  function findNote(info, picker) {
    return (
      (info && info.querySelector("[" + NOTE_ATTR + "]")) ||
      picker.querySelector("[" + NOTE_ATTR + "]")
    );
  }

  function noteText(picker, data, picks) {
    var current = data.products.get(data.current);
    var pairs = pairsOf(picks);
    var ghost = picks.some(function (pick) {
      return pick.ghost;
    });

    var others = 0;
    if (pairs.length && current && (ghost || !inStock(current, pairs))) {
      data.products.forEach(function (entry, handle) {
        if (handle !== data.current && inStock(entry, pairs)) others += 1;
      });
    }

    var picked = picks
      .map(function (pick) {
        return display(pick.label);
      })
      .join(", ");
    var colour = colourName(picker, data);
    var values = { picked: picked, color: colour, count: others };

    if (others) {
      var key =
        (ghost ? "notOfferedElsewhere" : "soldOutElsewhere") +
        (others === 1 ? "One" : "Other");
      return { text: fill(data.t[key], values), show: true };
    }
    // A remembered size nobody can sell right now: still say why nothing is picked.
    if (ghost) return { text: fill(data.t.notOffered, values), show: false };
    // Landed on (or clicked through to) a colour with nothing in stock: say so before a pick.
    if (!pairs.length && isDead(data))
      return { text: fill(data.t.soldOutEverywhere, values), show: false };
    // A remembered value where this colour comes in one value only (picked by the page).
        var only = SHOW_ONLY_COMES_IN ? onlyOnes(picker) : [];
    if (only.length) {
      return {
        text: only
          .map(function (o) {
            return fill(data.t.onlyComesIn, {
              picked: display(o.label),
              color: colour,
              only: display(o.only),
            });
          })
          .join(" "),
        show: false,
      };
    }
    return null;
  }

  // The note itself is not a live region (it is often inserted with its text, which screen
  // readers skip); changes are read out from one region that stays on the page.
  var lastSpoken = "";
  function speak(text) {
    if (!text || text === lastSpoken) {
      if (!text) lastSpoken = "";
      return;
    }
    lastSpoken = text;
    var region = document.querySelector("[data-ca-live]");
    if (!region) {
      region = document.createElement("div");
      region.setAttribute("data-ca-live", "");
      region.setAttribute("role", "status");
      region.setAttribute("aria-live", "polite");
      document.body.appendChild(region);
    }
    region.textContent = "";
    setTimeout(function () {
      if (lastSpoken === text) region.textContent = text;
    }, 80);
  }

  function updateNote(info, picker, data, picks) {
    if (!SHOW_AVAILABILITY_NOTE) {
      var old = findNote(info, picker);
      if (old) old.remove();
      speak("");
      return;
    }
    var note = findNote(info, picker);
    var message = noteText(picker, data, picks);

    var doc = (info || picker).ownerDocument || document;
    if (!note) {
      note = doc.createElement("p");
      note.setAttribute(NOTE_ATTR, "");
      var span = doc.createElement("span");
      var button = doc.createElement("button");
      button.type = "button";
      button.setAttribute(SHOW_ATTR, "");
      button.textContent = data.t.showColors;
      note.appendChild(span);
      note.appendChild(button);
      var spot = noteSpot(info, picker);
      spot.parent.insertBefore(note, spot.before);
    }
    note.toggleAttribute("data-ca-empty", !message);
    if (!message) {
      note.querySelector("span").textContent = "";
      note.querySelector("[" + SHOW_ATTR + "]").hidden = true;
      speak("");
      return;
    }
    var textEl = note.querySelector("span");
    if (textEl.textContent !== message.text) textEl.textContent = message.text;
    speak(message.text);
    var show = note.querySelector("[" + SHOW_ATTR + "]");
    if (show && show.hidden === message.show) show.hidden = !message.show;
  }

  // Scrolls to whichever colour row is showing (the mobile one lives outside the picker).
  function showColours(button) {
    var info = button.closest("product-info") || document;
    var rows = Array.prototype.filter.call(
      info.querySelectorAll(".swatches--color"),
      function (row) {
        return row.getClientRects().length > 0;
      },
    );
    if (!rows.length) return;
    rows[0].scrollIntoView({ behavior: "smooth", block: "center" });
  }

  /* ---------- applying ---------- */

  // A picker the theme is about to swap in (after a pick). Runs before it is painted, so the
  // marks are right the moment it shows. The mobile colour list sits outside the picker and
  // is never re-rendered, so it is updated here too, as is the line under the button.
  function applyIncoming(info, node) {
    if (!node || !node.querySelector || !node.querySelector(DATA_SELECTOR))
      return;
    var data = parseData(node);
    if (!data) return;
    var picks = picksFor(node, info);
    updateOptions(node, data, picks);
    updateColours(
      [node, info.querySelector(".product-info__mobile-form")],
      data,
      picks,
    );
    updateNote(info, node, data, picks);
  }

  function refresh() {
    Array.prototype.forEach.call(
      document.querySelectorAll("product-info"),
      function (info) {
        livePickers(info).forEach(function (picker) {
          var data = parseData(picker);
          if (!data) return;
          var picks = picksFor(picker, info);
          updateOptions(picker, data, picks);
          updateColours([info], data, picks);
          updateNote(info, picker, data, picks);
        });
      },
    );
  }

  var refreshQueued = false;
  function scheduleRefresh() {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(function () {
      refreshQueued = false;
      refresh();
    });
  }

  function hook() {
    Array.prototype.forEach.call(
      document.querySelectorAll("product-info"),
      function (info) {
        if (
          hookedInfos.has(info) ||
          !Array.isArray(info.preProcessHtmlCallbacks)
        )
          return;
        hookedInfos.add(info);
        info.preProcessHtmlCallbacks.push(function (node) {
          applyIncoming(info, node);
        });
      },
    );
  }

  /* ---------- remembered picks ---------- */

  function setGhost(name, value, silent) {
    if (name == null || value == null || value === "") return;
    ghosts.set(norm(name), { name: String(name), value: String(value) });
    if (!silent) refresh();
  }

  function clearGhost(name, silent) {
    if (!ghosts.delete(norm(name))) return;
    if (!silent) refresh();
  }

  // The picks to carry to another colour: [{ name, value }] by option name as the inputs carry
  // them. Real picks first; remembered ones for options without a real pick.
  function wanted() {
    var live = livePicker();
    var out = [];
    var seen = {};
    if (live) {
      picksFor(live.picker, live.info).forEach(function (pick) {
        if (seen[pick.name]) return;
        seen[pick.name] = true;
        out.push({ name: pick.rawName, value: pick.label });
      });
    }
    ghosts.forEach(function (ghost, key) {
      if (seen[key]) return;
      // A real pick of that option on this colour (a single-value option) replaces nothing:
      // the remembered value still applies to the next colour.
      seen[key] = true;
      out.push({ name: ghost.name, value: ghost.value });
    });
    return out;
  }

  // A tap on a stand-in box / row: remember that size and drop this colour's pick for the
  // option, so the button asks again and the line under it says where the size is.
  function pickPlaceholder(el) {
    var name = el.getAttribute("data-ca-option");
    var value = el.getAttribute("data-ca-placeholder");
    if (!name || value == null) return;

    var picker = el.closest("variant-picker");
    var fieldset = el.closest("fieldset[data-option-slug]");
    var slug = fieldset ? fieldset.getAttribute("data-option-slug") : null;
    var manager = window.addToCartButtonManager;
    var selection = selectionState();

    ghosts.set(norm(name), { name: name, value: value });

    if (selection && slug && GATED_SLUGS.indexOf(slug) !== -1) {
      selection.chosen.delete(slug);
      if (manager && typeof manager.refresh === "function") manager.refresh();
      else selection.sync();
    } else if (fieldset) {
      radiosOf(fieldset).forEach(function (radio) {
        radio.checked = false;
        radio.removeAttribute("checked");
      });
    }

    var dropdown = el.closest("size-dropdown");
    if (dropdown) {
      if (typeof dropdown.syncFromRadios === "function")
        dropdown.syncFromRadios();
      if (typeof dropdown.close === "function") dropdown.close();
    }

    refresh();
    if (picker && dropdown && dropdown.querySelector("[data-sd-trigger]")) {
      dropdown
        .querySelector("[data-sd-trigger]")
        .focus({ preventScroll: true });
    }
  }

  /* ---------- keeping the size across the theme's own colour switch ---------- */

  function rememberPicks() {
    var picks = wanted();
    clickToken += 1;
    lastClick = { token: clickToken, at: Date.now(), picks: picks };
  }

  // Wait for the theme to finish its own switch: it clears every pick 100 ms after the swap
  // and again on any re-render that lands while productColorSwatchHandler.isProductSwitching.
  function whenSwitchSettles(callback) {
    var start = Date.now();
    (function tick() {
      var handler = window.productColorSwatchHandler;
      var busy = !!(handler && handler.isProductSwitching);
      var elapsed = Date.now() - start;
      if ((!busy && elapsed >= SETTLE_MIN) || elapsed >= SETTLE_MAX) callback();
      else setTimeout(tick, 50);
    })();
  }

  function restorePicks(job) {
    if (job.token !== clickToken) return; // a newer colour click took over

    var selection = selectionState();
    var targets = [];
    var live = livePicker();

    if (live) {
      job.picks.forEach(function (pick) {
        var key = norm(pick.name);
        var fieldset = optionFieldsets(live.picker).filter(function (el) {
          return optionName(el) === key;
        })[0];
        if (!fieldset) return;
        var radios = radiosOf(fieldset);
        if (radios.length < 2) return;
        var slug = fieldset.getAttribute("data-option-slug");
        if (selection && selection.chosen.has(slug)) return; // the shopper already picked one here

        var radio = radios.filter(function (el) {
          return norm(el.value) === norm(pick.value);
        })[0];
        if (!radio) {
          setGhost(pick.name, pick.value, true); // this colour doesn't make that size/width
          return;
        }
        clearGhost(pick.name, true);
        targets.push(radio);
      });
    }

    pending = null;

    // The radios are set directly and a change event dispatched, which is what every listener
    // reacts to (the theme's picker, the add-to-cart gate, the mobile size list). Clicking the
    // label instead would make the browser focus the hidden radio and scroll the page down to
    // it. All radios are set first; the theme cancels all but the last section request.
    targets.forEach(function (radio) {
      radio.checked = true;
    });
    targets.forEach(function (radio) {
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    });

    refresh();
  }

  function onProductSwitched(event) {
    hook();

    // assets/instant-colorway.js has carried the picks already.
    if (event && event.detail && event.detail.source === "instant-colorway") {
      lastClick = null;
      pending = null;
      refresh();
      return;
    }

    var job = lastClick;
    lastClick = null;

    if (job && job.picks.length && Date.now() - job.at < CARRY_MAX_AGE) {
      pending = {};
      job.picks.forEach(function (pick) {
        pending[norm(pick.name)] = pick.value;
      });
      refresh(); // cross colours out for the carried size straight away
      whenSwitchSettles(function () {
        restorePicks(job);
      });
    } else {
      pending = null;
      refresh();
    }
  }

  /* ---------- wiring ---------- */

  function init() {
    hook();
    refresh();

    function managedOption(target) {
      if (!target || !target.closest) return null;
      var option = target.closest(
        'size-dropdown [role="option"], .label-swatch, input.custom-option-buttons',
      );
      var picker = option && option.closest("variant-picker");
      if (!picker || !picker.querySelector(DATA_SELECTOR)) return null;
      if (
        option.matches(PLACEHOLDER) ||
        option.classList.contains("is-ca-unavailable") ||
        option.hasAttribute("data-ca-unavailable")
      )
        return option;
      return null;
    }

    function chooseManagedOption(event, option) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (option.matches(PLACEHOLDER)) {
        pickPlaceholder(option);
        return;
      }
      var dropdown = option.closest("size-dropdown");
      if (dropdown) {
        dropdown.selectOption(option);
      } else {
        var fieldset = option.closest("fieldset");
        var radio = option.matches(OPTION_INPUT)
          ? option
          : fieldset.querySelector(
              'input[id="' + cssEscape(option.htmlFor) + '"]',
            );
        if (!radio) return;
        radio.checked = true;
        radio.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    window.addEventListener(
      "click",
      function (event) {
        var option = managedOption(event.target);
        if (option) chooseManagedOption(event, option);
      },
      true,
    );

    window.addEventListener(
      "keydown",
      function (event) {
        if (
          event.key !== "Enter" &&
          event.key !== " " &&
          event.key !== "Spacebar"
        )
          return;
        var target = event.target;
        if (target && target.matches && target.matches("[data-sd-panel]")) {
          target = document.getElementById(
            target.getAttribute("aria-activedescendant"),
          );
        }
        var option = managedOption(target);
        if (option) chooseManagedOption(event, option);
      },
      true,
    );

    // Capture phase, after add-to-cart-button.js has recorded the pick (it registered first),
    // so colours update the moment a size is tapped rather than after the section request.
    // Picking a value of an option forgets the remembered one.
    document.addEventListener(
      "change",
      function (event) {
        var target = event.target;
        if (!target || !target.matches || !target.matches(OPTION_INPUT)) return;
        if (target.checked)
          clearGhost(target.getAttribute("data-opt-name") || target.name, true);
        scheduleRefresh();
      },
      true,
    );

    // The theme's own colour switch clears the picks, so they are read on the click. The
    // instant switch stops the click before it gets here and carries them itself.
    document.addEventListener(
      "click",
      function (event) {
        var swatch =
          event.target && event.target.closest
            ? event.target.closest(SWATCH_SELECTOR)
            : null;
        if (swatch && !instantSwitch()) rememberPicks();
      },
      true,
    );

    document.addEventListener("click", function (event) {
      var target = event.target && event.target.closest ? event.target : null;
      if (!target) return;
      var button = target.closest("[" + SHOW_ATTR + "]");
      if (button) {
        showColours(button);
        return;
      }
      var stand = target.closest(PLACEHOLDER);
      if (stand && stand.closest("product-info")) pickPlaceholder(stand);
    });

    // Keyboard: Enter / Space on a stand-in box, or on the size list while a stand-in row is
    // the active one.
    document.addEventListener("keydown", function (event) {
      if (
        event.key !== "Enter" &&
        event.key !== " " &&
        event.key !== "Spacebar"
      )
        return;
      var target = event.target;
      if (!target || !target.closest) return;
      var stand = target.matches(PLACEHOLDER) ? target : null;
      if (!stand && target.matches("[data-sd-panel]")) {
        var active = target.getAttribute("aria-activedescendant");
        var row = active ? document.getElementById(active) : null;
        if (row && row.matches(PLACEHOLDER)) stand = row;
      }
      if (!stand || !stand.closest("product-info")) return;
      event.preventDefault();
      pickPlaceholder(stand);
    });

    document.addEventListener("product:switched", onProductSwitched);

    var pubsub = window.theme && window.theme.pubsub;
    if (
      pubsub &&
      pubsub.PUB_SUB_EVENTS &&
      pubsub.PUB_SUB_EVENTS.variantChange
    ) {
      pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, scheduleRefresh);
    }

    // Any other way the picker gets replaced (the colour switch swaps the whole section).
    // Mutation callbacks run before the next paint.
    if ("MutationObserver" in window && document.body) {
      new MutationObserver(function (records) {
        var swapped = records.some(function (record) {
          return Array.prototype.some.call(record.addedNodes, function (node) {
            return (
              node.nodeType === 1 &&
              (node.matches("variant-picker, product-info") ||
                !!node.querySelector("variant-picker, product-info"))
            );
          });
        });
        if (!swapped) return;
        hook();
        scheduleRefresh();
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  window.AetrexColorAvailability = {
    refresh: refresh,
    wanted: wanted,
    setGhost: setGhost,
    clearGhost: clearGhost,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
